import NotificacionesService from "@/services/NotificacionesService";

/**
 * Vuex module for the notification center + bell (Trello #264).
 *
 * The bell (NotificationBadgeDashboard.vue, always mounted, latest ~50) and
 * the center (NotificacionesCentroView.vue, paginated, limit=10) read
 * DIFFERENT lists — paging the center must never truncate what the bell
 * shows. Only `totalNoLeidas` is shared, so marking a notification read (or
 * deleting it) from either place reflects everywhere (SC-29).
 */

const BELL_LIMIT = 50;

const state = () => ({
  bellItems: [],
  bellLoading: false,
  bellError: null,

  centerItems: [],
  centerTotal: 0,
  centerOffset: 0,
  centerLimit: 10,
  centerLoading: false,
  centerError: null,

  totalNoLeidas: 0,

  config: [],
  configLoading: false,
  configError: null,
  acceso: { centro_roles: [], config_roles: [] },
  accesoLoading: false,
  accesoError: null,
  roles: [],
  rolesLoading: false,
});

const getters = {
  hayNoLeidas: (state) => state.totalNoLeidas > 0,
};

const actions = {
  /** Fetches the bell's latest notifications (fixed window, no pagination). */
  async fetchBellNotificaciones({ commit }) {
    try {
      commit("SET_BELL_LOADING", true);
      const response = await NotificacionesService.getNotificaciones({
        offset: 0,
        limit: BELL_LIMIT,
      });
      const { notificaciones, total_no_leidas } = response.data;
      commit("SET_BELL_ITEMS", notificaciones || []);
      commit("SET_TOTAL_NO_LEIDAS", total_no_leidas || 0);
      commit("SET_BELL_ERROR", null);
      return response.data;
    } catch (error) {
      commit("SET_BELL_ERROR", error.message || "Error al cargar notificaciones");
      throw error;
    } finally {
      commit("SET_BELL_LOADING", false);
    }
  },

  /** Fetches a page of the center's notifications. */
  async fetchCenterNotificaciones({ commit }, { offset = 0, limit = 10 } = {}) {
    try {
      commit("SET_CENTER_LOADING", true);
      const response = await NotificacionesService.getNotificaciones({ offset, limit });
      const { notificaciones, total_no_leidas, total } = response.data;
      commit("SET_CENTER_ITEMS", notificaciones || []);
      commit("SET_TOTAL_NO_LEIDAS", total_no_leidas || 0);
      commit("SET_CENTER_TOTAL", total || 0);
      commit("SET_CENTER_PAGINATION", { offset, limit });
      commit("SET_CENTER_ERROR", null);
      return response.data;
    } catch (error) {
      commit("SET_CENTER_ERROR", error.message || "Error al cargar notificaciones");
      throw error;
    } finally {
      commit("SET_CENTER_LOADING", false);
    }
  },

  async marcarVistos({ commit }, ids) {
    await NotificacionesService.marcarVistos(ids);
    commit("MARK_VISTAS", ids);
  },

  async marcarTodosVistos({ commit }) {
    await NotificacionesService.marcarTodosVistos();
    commit("MARK_TODOS_VISTAS");
  },

  async eliminar({ commit }, id) {
    await NotificacionesService.eliminar(id);
    commit("REMOVE_NOTIFICACION", id);
  },

  /**
   * Handles a live 'notificacionUsuario' socket event (SC-26). Delivery is
   * already per-user-room server-side, but we defensively re-check the
   * destination id so a stray event for another user never mutates the store.
   */
  recibirNotificacionEnVivo({ commit }, notificacion) {
    const usuario = JSON.parse(localStorage.getItem("token") || "{}");
    const esParaEsteUsuario =
      usuario?.id_usuario && notificacion?.id_usuario_destino === usuario.id_usuario;
    if (!esParaEsteUsuario) return;

    commit("PREPEND_NOTIFICACION", notificacion);
    commit("INCREMENT_TOTAL_NO_LEIDAS");
  },

  /** Fetches the full tipo -> roles notification matrix (admin-only, PR3). */
  async fetchConfig({ commit }) {
    try {
      commit("SET_CONFIG_LOADING", true);
      const response = await NotificacionesService.getConfig();
      commit("SET_CONFIG", response.data.config || []);
      commit("SET_CONFIG_ERROR", null);
      return response.data;
    } catch (error) {
      commit("SET_CONFIG_ERROR", error.message || "Error al cargar la configuración");
      throw error;
    } finally {
      commit("SET_CONFIG_LOADING", false);
    }
  },

  /** Updates one tipo's role matrix / activo flag (route key is `tipo`). */
  async actualizarConfig({ commit }, { tipo, payload }) {
    const response = await NotificacionesService.actualizarConfig(tipo, payload);
    commit("UPDATE_CONFIG_ROW", response.data.config);
    return response.data;
  },

  /** Fetches who sees the center (centro_roles) and who administers config (config_roles). */
  async fetchAcceso({ commit }) {
    try {
      commit("SET_ACCESO_LOADING", true);
      const response = await NotificacionesService.getAcceso();
      commit("SET_ACCESO", response.data);
      commit("SET_ACCESO_ERROR", null);
      return response.data;
    } catch (error) {
      commit("SET_ACCESO_ERROR", error.message || "Error al cargar el acceso");
      throw error;
    } finally {
      commit("SET_ACCESO_LOADING", false);
    }
  },

  /**
   * Updates centro/config role access. Backend rejects config_roles without
   * role 1 (anti-lockout) — that validation is enforced server-side.
   */
  async actualizarAcceso({ commit }, payload) {
    const response = await NotificacionesService.actualizarAcceso(payload);
    commit("SET_ACCESO", response.data.acceso);
    return response.data;
  },

  /** Fetches the role catalog used by the config/access role pickers. */
  async fetchRoles({ commit }) {
    try {
      commit("SET_ROLES_LOADING", true);
      const response = await NotificacionesService.getRoles();
      commit("SET_ROLES", response.data || []);
      return response.data;
    } finally {
      commit("SET_ROLES_LOADING", false);
    }
  },
};

/** Ids that are still unread in at least one of the given lists. */
function idsNoLeidos(ids, listas) {
  const encontrados = new Set();
  listas.forEach((items) => {
    items.forEach((n) => {
      if (ids.includes(n.id_notificacion) && !n.visto) {
        encontrados.add(n.id_notificacion);
      }
    });
  });
  return encontrados;
}

/** Returns a copy of items with the matching ids flagged as visto. */
function marcarVistosEnLista(items, ids) {
  return items.map((n) => (ids.includes(n.id_notificacion) ? { ...n, visto: 1 } : n));
}

const mutations = {
  SET_BELL_ITEMS(state, items) {
    state.bellItems = items;
  },
  SET_BELL_LOADING(state, value) {
    state.bellLoading = value;
  },
  SET_BELL_ERROR(state, error) {
    state.bellError = error;
  },
  SET_CENTER_ITEMS(state, items) {
    state.centerItems = items;
  },
  SET_CENTER_TOTAL(state, total) {
    state.centerTotal = total;
  },
  SET_CENTER_PAGINATION(state, { offset, limit }) {
    state.centerOffset = offset;
    state.centerLimit = limit;
  },
  SET_CENTER_LOADING(state, value) {
    state.centerLoading = value;
  },
  SET_CENTER_ERROR(state, error) {
    state.centerError = error;
  },
  SET_TOTAL_NO_LEIDAS(state, total) {
    state.totalNoLeidas = total;
  },
  INCREMENT_TOTAL_NO_LEIDAS(state) {
    state.totalNoLeidas += 1;
  },
  /** Prepends a live-arrived notification to the bell, and to the center only when it's on page 1. */
  PREPEND_NOTIFICACION(state, notificacion) {
    state.bellItems = [notificacion, ...state.bellItems];
    if (state.centerOffset === 0) {
      state.centerItems = [notificacion, ...state.centerItems];
    }
    state.centerTotal += 1;
  },
  MARK_VISTAS(state, ids) {
    const noLeidos = idsNoLeidos(ids, [state.bellItems, state.centerItems]);
    state.bellItems = marcarVistosEnLista(state.bellItems, ids);
    state.centerItems = marcarVistosEnLista(state.centerItems, ids);
    state.totalNoLeidas = Math.max(0, state.totalNoLeidas - noLeidos.size);
  },
  MARK_TODOS_VISTAS(state) {
    state.bellItems = state.bellItems.map((n) => ({ ...n, visto: 1 }));
    state.centerItems = state.centerItems.map((n) => ({ ...n, visto: 1 }));
    state.totalNoLeidas = 0;
  },
  REMOVE_NOTIFICACION(state, id) {
    const eliminada =
      state.centerItems.find((n) => n.id_notificacion === id) ||
      state.bellItems.find((n) => n.id_notificacion === id);
    state.bellItems = state.bellItems.filter((n) => n.id_notificacion !== id);
    state.centerItems = state.centerItems.filter((n) => n.id_notificacion !== id);
    if (eliminada && !eliminada.visto) {
      state.totalNoLeidas = Math.max(0, state.totalNoLeidas - 1);
    }
    state.centerTotal = Math.max(0, state.centerTotal - 1);
  },
  SET_CONFIG(state, config) {
    state.config = config;
  },
  SET_CONFIG_LOADING(state, value) {
    state.configLoading = value;
  },
  SET_CONFIG_ERROR(state, error) {
    state.configError = error;
  },
  UPDATE_CONFIG_ROW(state, row) {
    state.config = state.config.map((c) => (c.tipo === row.tipo ? row : c));
  },
  SET_ACCESO(state, acceso) {
    state.acceso = acceso;
  },
  SET_ACCESO_LOADING(state, value) {
    state.accesoLoading = value;
  },
  SET_ACCESO_ERROR(state, error) {
    state.accesoError = error;
  },
  SET_ROLES(state, roles) {
    state.roles = roles;
  },
  SET_ROLES_LOADING(state, value) {
    state.rolesLoading = value;
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
